/**
 * Confirmation Modal
 * Reusable confirmation modal for destructive actions
 */

import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    align-items: center;
  }
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 22px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
  border: 1px solid var(--border-color);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-width: min(500px, calc(100vw - 2rem));
    max-height: calc(100vh - 2rem);
    border-radius: 22px;
    padding-bottom: 0;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: calc(1rem + env(safe-area-inset-top));
  }
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: clamp(1.125rem, 1.5vw + 1rem, 1.25rem);
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 12px;
  transition: var(--transition);
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;

  &:hover {
    background: var(--background-tertiary);
    color: var(--text-primary);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const WarningText = styled.div`
  color: var(--text-primary);
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1rem;
`;

const WarningDetails = styled.div`
  background: linear-gradient(135deg, rgba(252, 129, 129, 0.12) 0%, rgba(245, 101, 101, 0.06) 100%);
  border: 1px solid rgba(252, 129, 129, 0.45);
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const WarningDetailsTitle = styled.div`
  color: var(--error-color);
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const WarningDetailsText = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.4;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    padding: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom));
    flex-direction: column-reverse;
    gap: 0.5rem;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border: 1px solid transparent;
  border-radius: 999px;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: 44px;
  touch-action: manipulation;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem 1.25rem;
    font-size: 0.875rem;
  }

  &.secondary {
    background: var(--background-primary);
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover {
      background: var(--background-tertiary);
      border-color: var(--border-dark);
      color: var(--text-primary);
    }
  }

  &.danger {
    background: linear-gradient(135deg, var(--error-color) 0%, var(--error-dark) 100%);
    color: var(--text-inverse);
    border-color: var(--error-color);
    box-shadow: 0 2px 8px rgba(252, 129, 129, 0.3);

    &:hover {
      background: linear-gradient(135deg, var(--error-dark) 0%, var(--error-color) 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(252, 129, 129, 0.4);
    }

    &:disabled {
      background: var(--error-light);
      border-color: var(--error-light);
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  &.warning {
    background: linear-gradient(135deg, var(--warning-color) 0%, var(--warning-dark) 100%);
    color: var(--text-inverse);
    border-color: var(--warning-color);
    box-shadow: 0 2px 8px rgba(214, 158, 46, 0.3);

    &:hover {
      background: linear-gradient(135deg, var(--warning-dark) 0%, var(--warning-color) 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(214, 158, 46, 0.4);
    }
  }
`;

function ConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  details = null,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isLoading = false,
  type = "danger"
}) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>
              <FiAlertTriangle size={20} />
              {title}
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <FiX size={18} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <WarningText>
              {message}
            </WarningText>

            {details && (
              <WarningDetails>
                <WarningDetailsTitle>
                  <FiAlertTriangle size={14} />
                  Important Notice
                </WarningDetailsTitle>
                <WarningDetailsText>
                  {details}
                </WarningDetailsText>
              </WarningDetails>
            )}
          </ModalBody>

          <ModalFooter>
            {cancelText && (
            <Button 
              className="secondary" 
              onClick={onClose}
              disabled={isLoading}
            >
              {cancelText}
            </Button>
            )}
            <Button 
              className={type}
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : confirmText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
  );
}

export default ConfirmationModal;
